/**
 * VaultItemCard component - displays an item as a wooden crate in the vault.
 * Each crate has the item image visible inside, with a paper label attached
 * showing the item name and stats.
 */

import type { Item, ItemStat } from '@/types/items';

interface VaultItemCardProps {
    /** The item to display */
    item: Item;
    /** Callback when the card is clicked */
    onClick?: () => void;
}

/**
 * Get the color for rarity ribbon/seal.
 */
function getRarityColor(rarity: string): { bg: string; border: string; text: string } {
    switch (rarity) {
        case 'mundane':
            return { bg: 'rgba(168, 162, 158, 0.9)', border: 'rgba(120, 113, 108, 1)', text: '#44403c' };
        case 'common':
            return { bg: 'rgba(156, 163, 175, 0.9)', border: 'rgba(107, 114, 128, 1)', text: '#374151' };
        case 'uncommon':
            return { bg: 'rgba(74, 222, 128, 0.9)', border: 'rgba(34, 197, 94, 1)', text: '#14532d' };
        case 'rare':
            return { bg: 'rgba(96, 165, 250, 0.9)', border: 'rgba(59, 130, 246, 1)', text: '#1e3a8a' };
        case 'epic':
            return { bg: 'rgba(192, 132, 252, 0.9)', border: 'rgba(168, 85, 247, 1)', text: '#3b0764' };
        case 'legendary':
            return { bg: 'rgba(251, 146, 60, 0.9)', border: 'rgba(249, 115, 22, 1)', text: '#431407' };
        case 'artifact':
            return { bg: 'rgba(253, 224, 71, 0.9)', border: 'rgba(234, 179, 8, 1)', text: '#422006' };
        case 'unique':
            return { bg: 'rgba(103, 232, 249, 0.9)', border: 'rgba(34, 211, 238, 1)', text: '#083344' };
        default:
            return { bg: 'rgba(156, 163, 175, 0.9)', border: 'rgba(107, 114, 128, 1)', text: '#374151' };
    }
}

/**
 * Displays an item as a wooden crate with attached paper label.
 * Clicking the crate opens the item detail modal.
 */
export function VaultItemCard({ item, onClick }: VaultItemCardProps) {
    const stats = Object.entries(item.stats) as [string, ItemStat][];
    const rarityColors = getRarityColor(item.rarity.value);

    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-md group"
        >
            {/* Wooden Crate Container */}
            <div
                className="relative rounded-md overflow-hidden transition-transform group-hover:scale-[1.02]"
                style={{
                    background: 'linear-gradient(145deg, #8B6914 0%, #6B4423 30%, #5D3A1A 70%, #4A2E15 100%)',
                    boxShadow: 'inset 0 2px 4px rgba(139, 105, 20, 0.3), inset 0 -2px 4px rgba(0,0,0,0.4), 3px 4px 8px rgba(0,0,0,0.5)',
                    border: '3px solid #3D2512',
                }}
            >
                {/* Wood grain texture overlay */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-30"
                    style={{
                        backgroundImage: `repeating-linear-gradient(
                            90deg,
                            transparent 0px,
                            transparent 2px,
                            rgba(0,0,0,0.1) 2px,
                            rgba(0,0,0,0.1) 4px
                        )`,
                    }}
                />

                {/* Horizontal wood planks effect */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        backgroundImage: `
                            linear-gradient(180deg,
                                transparent 0%,
                                rgba(0,0,0,0.15) 49%,
                                rgba(139,105,20,0.2) 50%,
                                transparent 51%
                            )
                        `,
                        backgroundSize: '100% 33.33%',
                    }}
                />

                {/* Metal corner reinforcements */}
                <div className="absolute top-0 left-0 w-5 h-5 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 50%, #374151 100%)',
                            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                            boxShadow: 'inset 1px 1px 2px rgba(255,255,255,0.3)',
                        }}
                    />
                    {/* Rivet */}
                    <div
                        className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full"
                        style={{
                            background: 'radial-gradient(circle at 30% 30%, #9CA3AF, #4B5563)',
                        }}
                    />
                </div>
                <div className="absolute top-0 right-0 w-5 h-5 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: 'linear-gradient(225deg, #6B7280 0%, #4B5563 50%, #374151 100%)',
                            clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
                            boxShadow: 'inset -1px 1px 2px rgba(255,255,255,0.3)',
                        }}
                    />
                    <div
                        className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                        style={{
                            background: 'radial-gradient(circle at 30% 30%, #9CA3AF, #4B5563)',
                        }}
                    />
                </div>
                <div className="absolute bottom-0 left-0 w-5 h-5 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: 'linear-gradient(45deg, #6B7280 0%, #4B5563 50%, #374151 100%)',
                            clipPath: 'polygon(0 0, 0 100%, 100% 100%)',
                            boxShadow: 'inset 1px -1px 2px rgba(255,255,255,0.3)',
                        }}
                    />
                    <div
                        className="absolute bottom-1 left-1 w-1.5 h-1.5 rounded-full"
                        style={{
                            background: 'radial-gradient(circle at 30% 30%, #9CA3AF, #4B5563)',
                        }}
                    />
                </div>
                <div className="absolute bottom-0 right-0 w-5 h-5 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: 'linear-gradient(315deg, #6B7280 0%, #4B5563 50%, #374151 100%)',
                            clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
                            boxShadow: 'inset -1px -1px 2px rgba(255,255,255,0.3)',
                        }}
                    />
                    <div
                        className="absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full"
                        style={{
                            background: 'radial-gradient(circle at 30% 30%, #9CA3AF, #4B5563)',
                        }}
                    />
                </div>

                {/* Item display area (inside the crate) */}
                <div className="relative p-3">
                    {/* Item image container - looks like item is sitting in straw/packing */}
                    <div
                        className="relative aspect-square rounded overflow-hidden mb-2"
                        style={{
                            background: 'linear-gradient(180deg, #2D1F0F 0%, #1a1408 100%)',
                            boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.6)',
                        }}
                    >
                        {/* Straw/packing material effect */}
                        <div
                            className="absolute inset-0 opacity-20 pointer-events-none"
                            style={{
                                backgroundImage: `repeating-linear-gradient(
                                    45deg,
                                    transparent 0px,
                                    transparent 2px,
                                    rgba(218, 165, 32, 0.3) 2px,
                                    rgba(218, 165, 32, 0.3) 4px
                                ),
                                repeating-linear-gradient(
                                    -45deg,
                                    transparent 0px,
                                    transparent 2px,
                                    rgba(184, 134, 11, 0.3) 2px,
                                    rgba(184, 134, 11, 0.3) 4px
                                )`,
                            }}
                        />

                        {item.image.small ? (
                            <img
                                src={item.image.small}
                                alt={item.name}
                                className="relative w-full h-full object-cover"
                            />
                        ) : (
                            <div className="relative w-full h-full flex items-center justify-center">
                                <svg
                                    className="w-12 h-12 text-amber-900/50"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                    />
                                </svg>
                            </div>
                        )}

                        {/* Quality stars overlay */}
                        <div
                            className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-xs font-medium"
                            style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                color: '#FCD34D',
                            }}
                        >
                            {item.quality_stars}
                        </div>

                        {/* Blueprint badge */}
                        {item.is_blueprint && (
                            <div
                                className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium"
                                style={{
                                    background: 'rgba(8, 145, 178, 0.9)',
                                    color: '#fff',
                                }}
                            >
                                Blueprint
                            </div>
                        )}
                    </div>

                    {/* Paper label attached to crate */}
                    <div
                        className="relative"
                        style={{
                            transform: 'rotate(-1deg)',
                        }}
                    >
                        {/* Paper/parchment label */}
                        <div
                            className="relative p-2 rounded-sm"
                            style={{
                                background: 'linear-gradient(145deg, #f5edd6 0%, #e8dcc4 50%, #ddd0b8 100%)',
                                boxShadow: '2px 2px 4px rgba(0,0,0,0.3), inset 0 0 20px rgba(139,115,85,0.1)',
                                border: '1px solid #c9b896',
                            }}
                        >
                            {/* Aged paper texture */}
                            <div
                                className="absolute inset-0 opacity-10 pointer-events-none rounded-sm"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
                                }}
                            />

                            {/* Rarity ribbon/seal */}
                            <div
                                className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                                style={{
                                    background: `radial-gradient(circle at 30% 30%, ${rarityColors.bg}, ${rarityColors.border})`,
                                    boxShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                                    border: `2px solid ${rarityColors.border}`,
                                }}
                            />

                            {/* Item name */}
                            <h3
                                className="font-serif font-bold text-sm truncate mb-0.5"
                                style={{ color: '#5D4028' }}
                            >
                                {item.name}
                            </h3>

                            {/* Rarity label */}
                            <p
                                className="text-xs mb-1.5 font-serif italic"
                                style={{ color: '#8B7355' }}
                            >
                                {item.rarity.label}
                            </p>

                            {/* Stats section */}
                            <div
                                className="space-y-0.5 pt-1.5 border-t"
                                style={{ borderColor: 'rgba(139, 115, 85, 0.3)' }}
                            >
                                {stats.length > 0 ? (
                                    <>
                                        {stats.slice(0, 3).map(([key, stat]) => (
                                            <div key={key} className="flex justify-between text-xs font-serif">
                                                <span style={{ color: '#6B5B47' }}>{stat.label}</span>
                                                <span className="font-semibold" style={{ color: '#2D5016' }}>+{stat.value}</span>
                                            </div>
                                        ))}
                                        {stats.length > 3 && (
                                            <p className="text-xs font-serif italic" style={{ color: '#8B7355' }}>
                                                +{stats.length - 3} more
                                            </p>
                                        )}
                                    </>
                                ) : item.recipe_fixed_stats ? (
                                    <>
                                        {Object.entries(item.recipe_fixed_stats).slice(0, 3).map(([key, stat]) => (
                                            <div key={key} className="flex justify-between text-xs font-serif">
                                                <span style={{ color: '#6B5B47' }}>{stat.label}</span>
                                                <span className="font-semibold" style={{ color: '#2D5016' }}>+{stat.value}</span>
                                            </div>
                                        ))}
                                        {Object.keys(item.recipe_fixed_stats).length > 3 && (
                                            <p className="text-xs font-serif italic" style={{ color: '#8B7355' }}>
                                                +{Object.keys(item.recipe_fixed_stats).length - 3} more
                                            </p>
                                        )}
                                    </>
                                ) : item.recipe_allowed_stats ? (
                                    <p className="text-xs font-serif italic" style={{ color: '#8B7355' }}>
                                        {Object.keys(item.recipe_allowed_stats).length} possible bonuses
                                    </p>
                                ) : (
                                    <p className="text-xs font-serif italic" style={{ color: '#8B7355' }}>
                                        No stats
                                    </p>
                                )}

                                {/* Total stat bonus */}
                                <div
                                    className="flex justify-between text-xs font-serif pt-1 mt-1 border-t"
                                    style={{ borderColor: 'rgba(139, 115, 85, 0.3)' }}
                                >
                                    <span style={{ color: '#6B5B47' }}>Total</span>
                                    <span className="font-bold" style={{ color: '#6B21A8' }}>
                                        +{item.total_stat_bonus > 0
                                            ? item.total_stat_bonus
                                            : item.recipe_fixed_stats
                                                ? Object.values(item.recipe_fixed_stats).reduce((sum, stat) => sum + stat.value, 0)
                                                : 0}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Tape/string holding the label */}
                        <div
                            className="absolute -top-1 left-1/2 -translate-x-1/2 w-8 h-2"
                            style={{
                                background: 'linear-gradient(180deg, rgba(210,190,140,0.9) 0%, rgba(195,175,125,0.85) 100%)',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                borderRadius: '1px',
                            }}
                        />
                    </div>
                </div>
            </div>
        </button>
    );
}

export default VaultItemCard;
